import { fromPromise, setup } from "xstate";

import { generateShortUuid } from "@moment/api-collab/id";
import { Logger } from "@moment/logging";

import { create, getPageDirPath, getPageFilePath, mkdir } from "~/data/filesystem";
import type { MachineError, PageMetadata } from "~/state/types";

const logger = new Logger("createPageMachine");

export const createPageMachine = setup({
	types: {
		context: {} as {
			documentPath: string;
			pageMetadata: PageMetadata;
			error?: MachineError;
		},
		input: {} as {
			documentPath: string;
			pageMetadata?: Partial<PageMetadata>;
		},
		output: {} as {
			pageMetadata: PageMetadata;
			error?: MachineError;
		},
	},
	actors: {
		createDirectory: fromPromise<string, { documentPath: string; pageId: string }>(
			async ({ input }) => {
				const pageDirectory = await getPageDirPath(input.documentPath, input.pageId);
				await mkdir(pageDirectory, {
					recursive: true,
				});

				return pageDirectory;
			}
		),
		createPageFile: fromPromise<void, { documentPath: string; pageId: string }>(
			async ({ input }) => {
				const pageFilePath = await getPageFilePath(input.documentPath, input.pageId);
				await create(pageFilePath);
			}
		),
	},
}).createMachine({
	id: "create-page",
	context: ({ input }) => {
		const id = generateShortUuid();
		return {
			documentPath: input.documentPath,
			pageMetadata: {
				id,
				title: "",
				slug: id,
				archived: false,
				parentPageID: null,
				// Override any properties from input.pageMetadata
				...input.pageMetadata,
			},
		};
	},
	initial: "creatingDirectory",
	states: {
		creatingDirectory: {
			invoke: {
				src: "createDirectory",
				input: ({ context }) => ({
					documentPath: context.documentPath,
					pageId: context.pageMetadata.id,
				}),
				onError: {
					actions: ({ event }) => {
						logger.error("Error creating page directory", {
							event,
						});
						throw new Error("Error creating page directory", { cause: event.error });
					},
					target: "done",
				},
				onDone: {
					target: "creatingPageFile",
					input: ({ context }) => ({
						documentPath: context.documentPath,
						pageId: context.pageMetadata.id,
					}),
				},
			},
		},
		creatingPageFile: {
			invoke: {
				src: "createPageFile",
				input: ({ context }) => ({
					documentPath: context.documentPath,
					pageId: context.pageMetadata.id,
				}),
				onError: {
					actions: ({ event }) => {
						logger.error("Error creating page file", {
							event,
						});
						throw new Error("Error creating page file", { cause: event.error });
					},
					target: "done",
				},
				onDone: {
					target: "done",
				},
			},
		},
		done: {
			type: "final",
		},
	},
	output: ({ context }) => ({
		pageMetadata: context.pageMetadata,
		error: context.error,
	}),
});
